import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

function BuyerHome({ user }) {
  const [topMatches, setTopMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadBuyerData(); }, []);

  const loadBuyerData = async () => {
    try {
      if (user?.buyer_id) {
        const boardRes = await authFetch(`${API}/api/board/buyer/${user.buyer_id}`);
        if (boardRes.ok) {
          const data = await boardRes.json();
          setTopMatches(Array.isArray(data) ? data.slice(0, 5) : []);
        }
      }
    } catch (err) {
      console.error('Buyer dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (p) => p ? `$${Number(p).toLocaleString()}` : 'N/A';

  const getTierColor = (tier) => {
    const colors = {
      'ELITE FIT': 'text-emerald-300',
      'STRONG FIT': 'text-green-300',
      'GOOD FIT': 'text-yellow-300',
      'MARGINAL FIT': 'text-orange-300',
      'POOR FIT': 'text-red-300',
    };
    return colors[tier] || 'text-gray-400';
  };

  const firstName = user?.full_name?.split(' ')[0] || 'there';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF]">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
          Welcome back, <span className="text-[#00E676]">{firstName}</span>
        </h1>
        <p className="text-[#B0BEC5] mb-8">Your commercial property search</p>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Link to="/match-board" className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 md:p-6 hover:border-[#00D4FF] transition-colors">
            <h3 className="text-white font-semibold text-lg mb-2">Your Match Board</h3>
            <p className="text-gray-400 text-sm mb-3">See all commercial properties ranked by how well they match your priorities.</p>
            <span className="text-[#00D4FF] text-sm font-medium">View Match Board →</span>
          </Link>
          <Link to={`/buyers/${user?.buyer_id}/criteria`} className="bg-[#1A2744] rounded-xl border border-[#00E676]/30 p-4 md:p-6 hover:border-[#00E676] transition-colors">
            <h3 className="text-white font-semibold text-lg mb-2">My Criteria & Weights</h3>
            <p className="text-gray-400 text-sm mb-3">Adjust what matters most to you — lease rate, square footage, location, and more.</p>
            <span className="text-[#00E676] text-sm font-medium">Edit My Criteria →</span>
          </Link>
        </div>

        {/* Top Matches */}
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-white font-semibold text-lg">Your Top Matches</h2>
            <Link to="/match-board" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View All →</Link>
          </div>

          {topMatches.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-2">No matches yet.</p>
              <p className="text-gray-500 text-sm">Set up your criteria and commercial properties will be scored automatically.</p>
              <Link to={`/buyers/${user?.buyer_id}/criteria`} className="inline-block mt-4 px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4]">
                Set Up My Criteria
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {topMatches.map((match, i) => (
                <Link key={match.property_id} to={`/properties/${match.property_id}/buyer/${user.buyer_id}`}
                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0 p-4 bg-[#0A0E27]/50 rounded-lg hover:bg-[#243B55]/30 transition-colors">
                  <div className="flex items-center gap-3 md:gap-4 min-w-0">
                    <span className="text-[#00D4FF] font-bold text-lg w-8 shrink-0">#{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate">{match.address}</div>
                      <div className="text-gray-500 text-xs">
                        {match.city}, {match.state} · {match.sqft?.toLocaleString()} sqft
                      </div>
                    </div>
                  </div>
                  <div className="text-right pl-11 md:pl-0">
                    <div className="text-[#00E676] font-bold">{formatPrice(match.price)}</div>
                    <div className={`text-sm font-medium ${getTierColor(match.tier)}`}>
                      {match.fit_score ? Math.round((match.fit_score / 9) * 100) : 0}% — {match.tier}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Tip */}
        <div className="mt-6 bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
          <p className="text-gray-400 text-sm">
            <span className="text-[#00D4FF] font-medium">Tip:</span> Adjust your criteria weights to see how different priorities change your property rankings. Properties are rescored automatically when you make changes.
          </p>
        </div>
      </div>
    </div>
  );
}

const ACTIVITY_STYLES = {
  buyer_signup:      { dot: 'bg-[#00E676]',           text: 'text-[#00E676]' },
  note_added:        { dot: 'bg-[#00D4FF]',           text: 'text-[#00D4FF]' },
  grade_override:    { dot: 'bg-green-400',           text: 'text-green-400' },
  report_generated:  { dot: 'bg-purple-400',          text: 'text-purple-400' },
  property_imported: { dot: 'bg-white',               text: 'text-white' },
  invite_created:    { dot: 'bg-blue-400',            text: 'text-blue-400' },
  invite_redeemed:   { dot: 'bg-[#00E676]',           text: 'text-[#00E676]' },
  criteria_updated:  { dot: 'bg-yellow-400',          text: 'text-yellow-400' },
};

// Compact icon mapping for the new condensed activity feed on Home.
const ACTIVITY_ICONS = {
  property_imported: '📄',
  buyer_signup:      '👤',
  invite_created:    '👤',
  invite_redeemed:   '👤',
  report_generated:  '📊',
  grade_override:    '⭐',
  criteria_updated:  '⭐',
  note_added:        '📝',
};

// "2h ago" style for the compact activity feed.
function shortRelativeTime(isoString) {
  if (!isoString) return '';
  const then = new Date(isoString.replace(' ', 'T'));
  if (isNaN(then.getTime())) return '';
  const s = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return then.toLocaleDateString();
}

// Readiness palette — Home dashboard uses a starker scheme than BuyerManagement,
// per spec (Hot=red, Warm=orange, Active=yellow, Early=blue, Cold=gray).
const READINESS_BADGE_HOME = {
  'Hot Lead':    { cls: 'bg-red-500/20 text-red-300 border-red-500/40',         bar: '#EF4444' },
  'Warm Lead':   { cls: 'bg-orange-500/20 text-orange-300 border-orange-500/40', bar: '#F97316' },
  'Active':      { cls: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40', bar: '#EAB308' },
  'Early Stage': { cls: 'bg-blue-500/20 text-blue-300 border-blue-500/40',       bar: '#3B82F6' },
  'Cold':        { cls: 'bg-gray-500/20 text-gray-300 border-gray-500/40',       bar: '#6B7280' },
};

const TIMELINE_LABELS_HOME = {
  '30_days': '30 days', '60_days': '60 days', '90_days': '90 days',
  '6_months': '6 months', 'exploring': 'Exploring', 'ready_now': 'Ready now',
};
const PREAPPROVAL_LABELS_HOME = {
  'approved': 'Funded', 'in_process': 'Financing', 'in_progress': 'Financing',
  'not_started': 'Not started', 'unknown': 'Unknown',
};

const HOME_LISTING_TYPE_BADGE = (lt) => {
  if (lt === 'for_sale')           return { label: 'FOR SALE',     cls: 'bg-green-900/50 text-green-300 border-green-600/40' };
  if (lt === 'for_sale_and_lease') return { label: 'SALE & LEASE', cls: 'bg-orange-900/50 text-orange-300 border-orange-600/40' };
  if (lt === 'for_lease')          return { label: 'FOR LEASE',    cls: 'bg-blue-900/50 text-blue-300 border-blue-600/40' };
  return null;
};

function relativeTime(isoString) {
  if (!isoString) return '';
  const then = new Date(isoString.replace(' ', 'T'));
  if (isNaN(then.getTime())) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr !== 1 ? 's' : ''} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return then.toLocaleDateString();
}

function ActivityItem({ item }) {
  const style = ACTIVITY_STYLES[item.activity_type] || { dot: 'bg-gray-400', text: 'text-gray-300' };
  const summary = item.summary || '';

  // Build the rendered summary by splicing in links for buyer_name and property_address when those substrings appear.
  const parts = [];
  let remaining = summary;
  const tokens = [];
  if (item.buyer_name && item.buyer_id) {
    tokens.push({
      text: item.buyer_name,
      el: <Link key="b" to={`/match-board?buyer=${item.buyer_id}`} className={`${style.text} hover:underline`}>{item.buyer_name}</Link>,
    });
  }
  if (item.property_address && item.property_id) {
    tokens.push({
      text: item.property_address,
      el: <Link key="p" to={`/properties/${item.property_id}`} className="text-white hover:underline">{item.property_address}</Link>,
    });
  }
  // Greedy left-to-right match: find earliest occurrence of any token, splice it.
  let safety = 0;
  while (remaining && safety < 10) {
    safety++;
    let bestIdx = -1, bestTok = null;
    for (const t of tokens) {
      const i = remaining.indexOf(t.text);
      if (i !== -1 && (bestIdx === -1 || i < bestIdx)) { bestIdx = i; bestTok = t; }
    }
    if (bestIdx === -1) {
      parts.push(remaining);
      remaining = '';
    } else {
      if (bestIdx > 0) parts.push(remaining.slice(0, bestIdx));
      parts.push(bestTok.el);
      remaining = remaining.slice(bestIdx + bestTok.text.length);
    }
  }
  if (remaining) parts.push(remaining);

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[#546E7A]/10 last:border-0">
      <span className={`shrink-0 w-2 h-2 rounded-full mt-2 ${style.dot}`}></span>
      <div className="flex-1 min-w-0">
        <p className="text-gray-200 text-sm leading-snug">{parts.map((p, i) => typeof p === 'string' ? <span key={i}>{p}</span> : p)}</p>
        <p className="text-gray-500 text-xs mt-0.5">{relativeTime(item.created_at)}</p>
      </div>
    </div>
  );
}

// Deal pipeline — compact segmented bar + short list of active deals.
// One row, no horizontal scrolling. Segments are proportional to deal count
// with a thin minimum so empty stages remain labeled.
const PIPELINE_STAGES = [
  { key: 'identified',     label: 'Identified', short: 'IDENT.',    color: '#546E7A', text: '#FFFFFF' },
  { key: 'touring',        label: 'Touring',    short: 'TOURING',   color: '#00D4FF', text: '#0A0E27' },
  { key: 'negotiating',    label: 'Negotiating',short: 'NEGOT.',    color: '#FFC107', text: '#0A0E27' },
  { key: 'loi',            label: 'LOI',        short: 'LOI',       color: '#FF6B35', text: '#FFFFFF' },
  { key: 'under_contract', label: 'Under Contract', short: 'CONTRACT', color: '#00E676', text: '#0A0E27' },
  { key: 'closed',         label: 'Closed',     short: 'CLOSED',    color: '#4CAF50', text: '#FFFFFF' },
];

// Stage badge styling for the deal list rows — same hue family as the bar.
const STAGE_BADGE = {
  identified:     { cls: 'border', style: { backgroundColor: 'rgba(84,110,122,0.25)', color: '#CFD8DC', borderColor: 'rgba(84,110,122,0.6)' } },
  touring:        { cls: 'border', style: { backgroundColor: 'rgba(0,212,255,0.18)',  color: '#00D4FF', borderColor: 'rgba(0,212,255,0.6)' } },
  negotiating:    { cls: 'border', style: { backgroundColor: 'rgba(255,193,7,0.18)',  color: '#FFC107', borderColor: 'rgba(255,193,7,0.6)' } },
  loi:            { cls: 'border', style: { backgroundColor: 'rgba(255,107,53,0.18)', color: '#FF6B35', borderColor: 'rgba(255,107,53,0.6)' } },
  under_contract: { cls: 'border', style: { backgroundColor: 'rgba(0,230,118,0.18)',  color: '#00E676', borderColor: 'rgba(0,230,118,0.6)' } },
  closed:         { cls: 'border', style: { backgroundColor: 'rgba(76,175,80,0.18)',  color: '#A5D6A7', borderColor: 'rgba(76,175,80,0.6)' } },
  lost:           { cls: 'border', style: { backgroundColor: 'rgba(239,68,68,0.18)',  color: '#FCA5A5', borderColor: 'rgba(239,68,68,0.6)' } },
};

function pipelineFitColor(pct) {
  if (pct == null) return { color: '#9CA3AF' };
  if (pct >= 75) return { color: '#00E676' };
  if (pct >= 60) return { color: '#00D4FF' };
  if (pct >= 45) return { color: '#FDE68A' };
  if (pct >= 30) return { color: '#FDBA74' };
  return { color: '#FCA5A5' };
}

function DealPipelineCard({ pipeline }) {
  const totals = (pipeline && pipeline.totals) || {};
  const lostCount = totals.lost || 0;
  const totalActive = PIPELINE_STAGES.reduce((sum, s) => sum + (totals[s.key] || 0), 0);

  // Flatten active deals across all non-identified, non-lost stages for the
  // "recent activity" list. We use days_in_stage as the recency proxy: smaller
  // means more recently moved.
  const activeDeals = [];
  PIPELINE_STAGES.forEach((stage) => {
    if (stage.key === 'identified') return;
    const list = (pipeline && pipeline[stage.key]) || [];
    list.forEach((d) => activeDeals.push(d));
  });
  activeDeals.sort((a, b) => {
    const da = a.days_in_stage == null ? 99999 : a.days_in_stage;
    const db = b.days_in_stage == null ? 99999 : b.days_in_stage;
    return da - db;
  });

  // Unique client count across all active (non-lost) deals
  const clientSet = new Set();
  PIPELINE_STAGES.forEach((stage) => {
    const list = (pipeline && pipeline[stage.key]) || [];
    list.forEach((d) => clientSet.add(d.buyer_id));
  });
  const clientCount = clientSet.size;

  // Proportional segment widths: scale count, with a small minimum so empty
  // stages remain visible.
  const segmentGrow = (n) => Math.max(0.6, n + 0.6);

  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#FF6B35] p-5 mb-6">
      <div className="flex justify-between items-start gap-3 mb-3 flex-wrap">
        <div>
          <h2 className="text-white font-semibold">Deal Pipeline</h2>
          <p className="text-gray-400 text-xs mt-0.5">
            {totalActive} active deal{totalActive === 1 ? '' : 's'} across {clientCount} client{clientCount === 1 ? '' : 's'}
            {lostCount > 0 && (
              <span className="text-gray-500"> · {lostCount} lost</span>
            )}
          </p>
        </div>
        {totalActive > 0 && (
          <Link to="/deals" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View All →</Link>
        )}
      </div>

      {/* Segmented pipeline bar — flex-wrap so on narrow screens segments wrap to 2 rows */}
      <div className="flex flex-wrap gap-1 mb-4 rounded-lg overflow-hidden">
        {PIPELINE_STAGES.map((stage) => {
          const count = totals[stage.key] || 0;
          return (
            <div
              key={stage.key}
              className="flex-1 min-w-[80px] px-2 sm:px-3 py-2 flex items-center justify-between gap-1.5 whitespace-nowrap overflow-hidden"
              style={{
                flexGrow: segmentGrow(count),
                backgroundColor: stage.color,
                color: stage.text,
                opacity: count === 0 ? 0.55 : 1,
              }}
              title={`${stage.label}: ${count}`}
            >
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wide truncate">
                <span className="hidden lg:inline">{stage.label}</span>
                <span className="lg:hidden">{stage.short}</span>
              </span>
              {count > 0 && (
                <span className="text-[10px] sm:text-xs font-bold tabular-nums shrink-0">{count}</span>
              )}
            </div>
          );
        })}
        {lostCount > 0 && (
          <div
            className="px-3 py-2 flex items-center gap-2"
            style={{ backgroundColor: '#7F1D1D', color: '#FFFFFF' }}
            title={`Lost: ${lostCount}`}
          >
            <span className="text-xs font-bold uppercase tracking-wide">Lost</span>
            <span className="text-xs font-bold tabular-nums">{lostCount}</span>
          </div>
        )}
      </div>

      {/* Compact deal list */}
      {totalActive === 0 ? (
        <p className="text-gray-500 text-sm">
          No active deals yet. Set a stage from any property evaluation page to start tracking.
        </p>
      ) : (
        <div className="divide-y divide-[#546E7A]/15">
          {activeDeals.slice(0, 5).map((d) => {
            const badge = STAGE_BADGE[d.stage] || STAGE_BADGE.identified;
            const fit = pipelineFitColor(d.fit_pct);
            return (
              <Link
                key={`${d.property_id}-${d.buyer_id}`}
                to={`/properties/${d.property_id}/buyer/${d.buyer_id}`}
                className="flex items-center gap-3 py-2 px-1 hover:bg-[#243B55]/30 transition-colors rounded"
              >
                <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
                  <span className="text-white text-sm font-medium truncate">
                    {d.property_address}
                  </span>
                  <span className="text-gray-500 text-xs truncate">· {d.buyer_name}</span>
                </div>
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide shrink-0 ${badge.cls}`}
                  style={badge.style}
                >
                  {d.stage_label || d.stage}
                </span>
                <span
                  className="text-sm font-bold tabular-nums shrink-0 w-10 text-right"
                  style={fit}
                >
                  {d.fit_pct != null ? `${d.fit_pct}%` : '—'}
                </span>
                <span className="text-gray-500 text-xs tabular-nums shrink-0 w-10 text-right">
                  {d.days_in_stage != null ? `${d.days_in_stage}d` : '—'}
                </span>
              </Link>
            );
          })}
          {activeDeals.length > 5 && (
            <div className="pt-2 text-xs text-gray-500 text-center">
              +{activeDeals.length - 5} more · <Link to="/deals" className="text-[#00D4FF] hover:text-[#00E676]">View All</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AgentHome({ user }) {
  const [buyers, setBuyers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [recentGrades, setRecentGrades] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [readiness, setReadiness] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);

  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState('');
  const [insightsExpanded, setInsightsExpanded] = useState(true);

  const loadInsights = async () => {
    setInsightsLoading(true);
    setInsightsError('');
    try {
      const res = await authFetch(`${API}/api/insights/daily`);
      const data = await res.json();
      if (!res.ok) {
        setInsightsError(data.detail || 'Could not generate insights');
      } else {
        setInsights(data);
        setInsightsExpanded(true);
      }
    } catch (err) {
      setInsightsError('Network error');
    } finally {
      setInsightsLoading(false);
    }
  };

  const handlePrintInsights = () => {
    if (!insights || !insights.actions || insights.actions.length === 0) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const today = new Date().toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    const agentName = escapeHtml(user?.full_name || 'Agent');

    const cards = insights.actions.map(a => {
      const p = Math.max(1, Math.min(5, parseInt(a.priority, 10) || 5));
      const buyer = a.buyer_name
        ? `<span class="buyer-name">${escapeHtml(a.buyer_name)}</span> `
        : '';
      const reasoning = a.reasoning
        ? `<p class="reasoning">${escapeHtml(a.reasoning)}</p>`
        : '';
      return `
        <div class="action-card priority-${p}">
          <div class="priority-num">${p}</div>
          <div class="action-content">
            <p class="action-text">${buyer}${escapeHtml(a.action)}</p>
            ${reasoning}
          </div>
        </div>`;
    }).join('');

    printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Daily Action Plan — ${agentName}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px;
      color: #1a1a1a;
      background: #ffffff;
      line-height: 1.5;
    }
    .header {
      border-bottom: 2px solid #00BCD4;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    .header .label {
      font-size: 11px;
      color: #888;
      letter-spacing: 2px;
      text-transform: uppercase;
    }
    .header h1 {
      font-size: 24px;
      margin: 6px 0 4px 0;
      color: #1a1a1a;
    }
    .header .date {
      color: #555;
      font-size: 14px;
    }
    .action-card {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      border-left: 4px solid #9CA3AF;
      background: #fafafa;
      padding: 14px 18px;
      margin-bottom: 12px;
      border-radius: 0 6px 6px 0;
      page-break-inside: avoid;
    }
    .action-card.priority-1 { border-left-color: #00C853; background: #f0fbf3; }
    .action-card.priority-2,
    .action-card.priority-3 { border-left-color: #0097A7; background: #f0fafc; }
    .action-card.priority-4,
    .action-card.priority-5 { border-left-color: #9CA3AF; background: #fafafa; }
    .priority-num {
      flex-shrink: 0;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      font-weight: 700;
      font-size: 13px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #ccc;
      background: #ffffff;
    }
    .priority-1 .priority-num { color: #00833D; border-color: #00C853; }
    .priority-2 .priority-num,
    .priority-3 .priority-num { color: #006978; border-color: #0097A7; }
    .priority-4 .priority-num,
    .priority-5 .priority-num { color: #555; border-color: #9CA3AF; }
    .action-content { flex: 1; min-width: 0; }
    .action-text {
      margin: 0 0 6px 0;
      font-size: 14px;
      color: #1a1a1a;
    }
    .buyer-name {
      font-weight: 600;
      color: #0097A7;
    }
    .reasoning {
      margin: 0;
      font-size: 12px;
      color: #555;
    }
    .footer {
      margin-top: 40px;
      padding-top: 16px;
      border-top: 1px solid #ddd;
      font-size: 11px;
      color: #999;
      text-align: center;
    }
    @media print {
      body { padding: 24px; }
      .action-card,
      .priority-num {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="label">SchemeSync CRE — Daily Action Plan</div>
    <h1>Daily Action Plan for ${agentName}</h1>
    <div class="date">${escapeHtml(today)}</div>
  </div>
  ${cards}
  <div class="footer">Generated by SchemeSync CRE — re.aida-advisory.com</div>
</body>
</html>`);
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const [buyersRes, propsRes, recentRes, activityRes, readinessRes, pipelineRes] = await Promise.all([
        authFetch(`${API}/api/buyers`),
        authFetch(`${API}/api/properties?limit=200`),
        authFetch(`${API}/api/grades/recent?limit=5`),
        authFetch(`${API}/api/activity?limit=16`),
        authFetch(`${API}/api/readiness/scores`).catch(() => null),
        authFetch(`${API}/api/deals/pipeline`).catch(() => null),
      ]);
      if (buyersRes.ok) setBuyers(await buyersRes.json());
      if (propsRes.ok) setProperties(await propsRes.json());
      if (recentRes.ok) setRecentGrades(await recentRes.json());
      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivity(data.slice(0, 15));
        setActivityHasMore(data.length > 15);
      }
      if (readinessRes && readinessRes.ok) setReadiness(await readinessRes.json());
      if (pipelineRes && pipelineRes.ok) setPipeline(await pipelineRes.json());
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const firstName = user?.full_name?.split(' ')[0] || 'there';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF]">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
          Welcome back, <span className="text-[#00E676]">{firstName}</span>
        </h1>
        <p className="text-[#B0BEC5] mb-6">Your Property Evaluation Dashboard</p>

        {/* AI Insights — Daily Brew */}
        <div className="mb-8">
          {!insights && !insightsLoading && !insightsError && (
            <button
              onClick={loadInsights}
              className="w-full md:w-auto px-5 py-3 bg-gradient-to-r from-[#00D4FF] to-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-[#00D4FF]/20"
            >
              ☕ Daily Brew
            </button>
          )}

          {insightsLoading && (
            <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 md:p-5">
              <div className="flex items-center gap-3">
                <span className="text-xl">✨</span>
                <div>
                  <div className="text-[#00D4FF] text-sm font-medium">Analyzing your portfolio...</div>
                  <div className="text-gray-500 text-xs mt-0.5">Looking at your clients, recent activity, and inventory.</div>
                </div>
              </div>
            </div>
          )}

          {insightsError && !insightsLoading && (
            <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm flex justify-between items-center gap-3 flex-wrap">
              <span>{insightsError}</span>
              <button onClick={loadInsights} className="text-red-200 underline hover:text-white text-xs">Retry</button>
            </div>
          )}

          {insights && !insightsLoading && (
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                {insights.actions.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setInsightsExpanded(v => !v)}
                    className="flex items-start gap-3 text-left hover:text-[#00D4FF] transition-colors"
                    aria-expanded={insightsExpanded}
                  >
                    <span className="text-2xl leading-none mt-0.5">☕</span>
                    <span>
                      <span className="text-white font-semibold block">
                        Daily Brew
                        {!insightsExpanded && ` (${insights.actions.length} action${insights.actions.length !== 1 ? 's' : ''})`}
                      </span>
                      <span className="text-gray-500 text-xs">Your AI-powered morning action plan</span>
                    </span>
                    <span className={`inline-block text-xs transition-transform duration-200 mt-1 ${insightsExpanded ? 'rotate-90' : ''}`}>▶</span>
                  </button>
                ) : (
                  <div className="flex items-start gap-3">
                    <span className="text-2xl leading-none mt-0.5">☕</span>
                    <div>
                      <h2 className="text-white font-semibold">Daily Brew</h2>
                      <p className="text-gray-500 text-xs">Your AI-powered morning action plan</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-4">
                  {insights.actions && insights.actions.length > 0 && (
                    <button onClick={handlePrintInsights} className="text-[#00D4FF] text-sm hover:text-[#00E676]">Print</button>
                  )}
                  <button onClick={loadInsights} className="text-[#00D4FF] text-sm hover:text-[#00E676]">Refresh</button>
                </div>
              </div>
              {insights.actions.length === 0 ? (
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5 text-gray-400 text-sm">
                  No specific actions to recommend right now.
                </div>
              ) : (
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${insightsExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="space-y-3">
                  {insights.actions.map((a, i) => {
                    const matched = a.buyer_name
                      ? buyers.find(b => (b.name || '').trim().toLowerCase() === a.buyer_name.trim().toLowerCase())
                      : null;
                    const borderClass =
                      a.priority === 1 ? 'border-l-[#00E676]' :
                      a.priority <= 3  ? 'border-l-[#00D4FF]' :
                                         'border-l-gray-500';
                    const numClass =
                      a.priority === 1 ? 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40' :
                      a.priority <= 3  ? 'bg-[#00D4FF]/20 text-[#00D4FF] border-[#00D4FF]/40' :
                                         'bg-gray-500/20 text-gray-400 border-gray-500/40';
                    return (
                      <div key={i} className={`bg-[#1A2744] rounded-r-xl border-l-4 ${borderClass} border-y border-r border-[#546E7A]/30 p-4`}>
                        <div className="flex items-start gap-3">
                          <div className={`shrink-0 w-7 h-7 rounded-full border ${numClass} flex items-center justify-center text-xs font-bold`}>
                            {a.priority}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm leading-snug">
                              {a.buyer_name && (
                                matched
                                  ? <Link to={`/match-board?buyer=${matched.id}`} className="text-[#00D4FF] hover:underline font-medium mr-1.5">{a.buyer_name}</Link>
                                  : <span className="text-[#00D4FF] font-medium mr-1.5">{a.buyer_name}</span>
                              )}
                              <span>{a.action}</span>
                            </p>
                            {a.reasoning && (
                              <p className="text-gray-500 text-xs mt-1.5">{a.reasoning}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
            <p className="text-gray-400 text-sm">Active Clients</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1">{buyers.length}</p>
            <Link to="/buyers" className="text-[#00D4FF] text-sm mt-2 inline-block hover:text-[#00E676]">Manage Clients →</Link>
          </div>
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
            <p className="text-gray-400 text-sm">Properties</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1">{properties.length}</p>
            {properties.length > 0 && (() => {
              const lease = properties.filter(p => p.listing_type === 'for_lease').length;
              const sale  = properties.filter(p => p.listing_type === 'for_sale').length;
              const both  = properties.filter(p => p.listing_type === 'for_sale_and_lease').length;
              return (
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs mt-1.5">
                  <span className="text-blue-300">{lease} For Lease</span>
                  <span className="text-gray-600">|</span>
                  <span className="text-green-300">{sale} For Sale</span>
                  <span className="text-gray-600">|</span>
                  <span className="text-orange-300">{both} Both</span>
                </div>
              );
            })()}
            <Link to="/properties" className="text-[#00D4FF] text-sm mt-2 inline-block hover:text-[#00E676]">Manage Properties →</Link>
          </div>
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
            <p className="text-gray-400 text-sm">Recent Evaluations</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1">{recentGrades.length}</p>
            <Link to="/match-board" className="text-[#00D4FF] text-sm mt-2 inline-block hover:text-[#00E676]">View Match Board →</Link>
          </div>
        </div>

        {/* ── ROW 3 — Client Pipeline (60%) + Top Matches (40%) ── */}
        {(() => {
          // Lookups so rows can pull richer property data than what readiness ships.
          const propsByAddress = {};
          properties.forEach(p => { if (p.address) propsByAddress[p.address] = p; });

          // Build the pipeline rows: one per buyer with their readiness info, sorted hot → cold.
          const pipeline = readiness
            .slice()
            .sort((a, b) => (b.readiness_score || 0) - (a.readiness_score || 0))
            .slice(0, 6);

          // Build "top matches" — one row per buyer using their best-fit property.
          const topMatchRows = readiness
            .filter(r => r.top_property_address && r.top_fit_score_pct != null)
            .slice()
            .sort((a, b) => (b.top_fit_score_pct || 0) - (a.top_fit_score_pct || 0))
            .slice(0, 5)
            .map(r => {
              const p = propsByAddress[r.top_property_address];
              return {
                buyer_id: r.buyer_id,
                buyer_name: r.buyer_name,
                property_id: p ? p.id : null,
                property_name: (p && p.property_name) || r.top_property_address,
                property_address: r.top_property_address,
                listing_type: p ? p.listing_type : null,
                fit_pct: r.top_fit_score_pct,
              };
            });

          const fitColor = (pct) => {
            if (pct >= 75) return { text: 'text-[#00E676]', bar: 'bg-[#00E676]' };
            if (pct >= 60) return { text: 'text-[#00D4FF]', bar: 'bg-[#00D4FF]' };
            if (pct >= 45) return { text: 'text-yellow-300', bar: 'bg-yellow-400' };
            if (pct >= 30) return { text: 'text-orange-300', bar: 'bg-orange-400' };
            return { text: 'text-red-300', bar: 'bg-red-500' };
          };

          return (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-6">
              {/* Client Pipeline (60%) */}
              <div className="lg:col-span-3 bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00D4FF] p-5">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-white font-semibold">Client Pipeline</h2>
                  <Link to="/buyers" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View All →</Link>
                </div>
                {pipeline.length === 0 ? (
                  <p className="text-gray-500 text-sm">Add clients to see your pipeline.</p>
                ) : (
                  <div className="space-y-2">
                    {pipeline.map(r => {
                      const badge = READINESS_BADGE_HOME[r.readiness_label] || READINESS_BADGE_HOME['Cold'];
                      const fit = r.top_fit_score_pct != null ? fitColor(r.top_fit_score_pct) : null;
                      const timelineTxt = TIMELINE_LABELS_HOME[r.timeline];
                      const financeTxt = PREAPPROVAL_LABELS_HOME[r.pre_approval_status];
                      return (
                        <Link key={r.buyer_id} to={`/match-board?buyer=${r.buyer_id}`}
                          className="block p-3 bg-[#0A0E27]/50 rounded-lg hover:bg-[#243B55]/40 transition-colors">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-white font-semibold text-sm truncate">{r.buyer_name}</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${badge.cls}`}>{r.readiness_label}</span>
                              </div>
                              <div className="text-gray-500 text-xs mt-0.5 flex flex-wrap gap-x-2">
                                {r.properties_scored != null && <span>{r.properties_scored} scored</span>}
                                {timelineTxt && <span>· {timelineTxt}</span>}
                                {financeTxt && <span>· {financeTxt}</span>}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              {fit ? (
                                <div className={`font-bold text-sm ${fit.text}`}>{r.top_fit_score_pct}%</div>
                              ) : (
                                <div className="text-gray-600 text-xs">—</div>
                              )}
                              <div className="text-gray-600 text-[10px]">top fit</div>
                            </div>
                          </div>
                          {fit && (
                            <div className="w-full bg-[#0A0E27] rounded-full h-1.5 mt-2 overflow-hidden">
                              <div className={`h-1.5 rounded-full ${fit.bar}`} style={{ width: `${r.top_fit_score_pct}%` }}></div>
                            </div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Top Matches (40%) */}
              <div className="lg:col-span-2 bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00E676] p-5">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-white font-semibold">Top Matches</h2>
                  <Link to="/agent-board" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View Match Board →</Link>
                </div>
                {topMatchRows.length === 0 ? (
                  <p className="text-gray-500 text-sm">No scored matches yet.</p>
                ) : (
                  <div className="space-y-2">
                    {topMatchRows.map((m, i) => {
                      const fit = fitColor(m.fit_pct);
                      const lt = HOME_LISTING_TYPE_BADGE(m.listing_type);
                      return (
                        <Link key={i} to={m.property_id ? `/properties/${m.property_id}/buyer/${m.buyer_id}` : `/match-board?buyer=${m.buyer_id}`}
                          className="block p-3 bg-[#0A0E27]/50 rounded-lg hover:bg-[#243B55]/40 transition-colors">
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="text-white text-sm font-medium truncate">{m.property_name}</div>
                              <div className="text-gray-500 text-xs truncate">{m.buyer_name}</div>
                              {lt && (
                                <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold border ${lt.cls}`}>{lt.label}</span>
                              )}
                            </div>
                            <div className={`px-2 py-1 rounded text-xs font-bold border shrink-0 ${
                              m.fit_pct >= 75 ? 'bg-[#00E676]/15 text-[#00E676] border-[#00E676]/40' :
                              m.fit_pct >= 60 ? 'bg-[#00D4FF]/15 text-[#00D4FF] border-[#00D4FF]/40' :
                              m.fit_pct >= 45 ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40' :
                                                'bg-orange-500/15 text-orange-300 border-orange-500/40'
                            }`}>{m.fit_pct}%</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── ROW 3.5 — Deal Pipeline ── */}
        <DealPipelineCard pipeline={pipeline} />

        {/* ── ROW 4 — Listing Inventory (left) + Compact Activity (right) ── */}
        {(() => {
          const leaseCount = properties.filter(p => p.listing_type === 'for_lease').length;
          const saleCount  = properties.filter(p => p.listing_type === 'for_sale').length;
          const bothCount  = properties.filter(p => p.listing_type === 'for_sale_and_lease').length;
          const totalCount = properties.length;
          const widthFor = (n) => totalCount > 0 ? `${Math.max(2, (n / totalCount) * 100)}%` : '0%';
          const compactActivity = activity.slice(0, 5);

          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
              {/* Listing Inventory */}
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#FF6B35] p-5">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-white font-semibold">Listing Inventory</h2>
                  <Link to="/properties" className="text-[#00D4FF] text-sm hover:text-[#00E676]">Manage Properties →</Link>
                </div>
                {totalCount === 0 ? (
                  <p className="text-gray-500 text-sm">No properties yet. Add one to start scoring.</p>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className="text-blue-300 font-medium">For Lease</span>
                        <span className="text-white">{leaseCount}</span>
                      </div>
                      <div className="w-full bg-[#0A0E27] rounded h-2.5 overflow-hidden">
                        <div className="h-2.5 rounded bg-blue-500" style={{ width: widthFor(leaseCount) }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className="text-green-300 font-medium">For Sale</span>
                        <span className="text-white">{saleCount}</span>
                      </div>
                      <div className="w-full bg-[#0A0E27] rounded h-2.5 overflow-hidden">
                        <div className="h-2.5 rounded bg-green-500" style={{ width: widthFor(saleCount) }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className="text-orange-300 font-medium">Sale &amp; Lease</span>
                        <span className="text-white">{bothCount}</span>
                      </div>
                      <div className="w-full bg-[#0A0E27] rounded h-2.5 overflow-hidden">
                        <div className="h-2.5 rounded bg-orange-500" style={{ width: widthFor(bothCount) }}></div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-[#546E7A]/20 mt-3">
                      <span className="text-gray-400 text-sm">Total</span>
                      <span className="text-white font-bold text-base">{totalCount}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Activity — compact */}
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00D4FF] p-5">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-white font-semibold">Recent Activity</h2>
                  {activityHasMore && (
                    <Link to="/agent-board" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View All →</Link>
                  )}
                </div>
                {compactActivity.length === 0 ? (
                  <p className="text-gray-500 text-sm">No activity yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {compactActivity.map(item => (
                      <div key={item.id} className="flex items-center gap-2 py-1 text-sm">
                        <span className="shrink-0 text-base leading-none">{ACTIVITY_ICONS[item.activity_type] || '•'}</span>
                        <span className="flex-1 text-gray-300 truncate">{item.summary}</span>
                        <span className="shrink-0 text-gray-600 text-xs">{shortRelativeTime(item.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Match Board CTA */}
        <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/20 p-6 md:p-8 text-center">
          <h2 className="text-white font-bold text-xl mb-2">Your Property Match Board</h2>
          <p className="text-gray-400 mb-4">View your ranked properties and AI evaluation reports.</p>
          <Link to="/match-board" className="inline-block px-6 py-3 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] transition-colors">
            View Match Board
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();

  // Brokers + super-admins land directly on the Brokerage Command Center.
  // The "/" route is the only entry-point that needs this redirect — the
  // separate /broker-dashboard route still works for direct links and the
  // legacy URL stays valid.
  if (user?.role === 'broker' || user?.role === 'super_admin') {
    return <Navigate to="/broker-dashboard" replace />;
  }

  if (user?.role === 'buyer') {
    return <BuyerHome user={user} />;
  }

  return <AgentHome user={user} />;
}
